/* ============================================================
   ShoLi — Menu Actions Module
   Data-driven menu action definitions and dispatch.
   Depends on State, ConfirmDialog, Toast.
   ============================================================ */

import type { AppSettings } from "../state";

// Forward declarations — provided by app.ts composition root
type StateApi = {
  getSetting: <K extends keyof AppSettings>(key: K) => AppSettings[K];
  checkAll: () => void;
  uncheckAll: () => void;
  markOffList: () => number;
  markAllOffList: () => number;
};

type ToastApi = {
  show: (message: string, duration?: number) => void;
};

type ConfirmDialogApi = {
  ask: (message: string) => Promise<boolean>;
};

interface MenuActionEntry {
  run: () => void;
  confirmSetting?: keyof AppSettings;
  confirmMessage?: string | undefined;
}

export class MenuActions {
  private state: StateApi;
  private confirm: ConfirmDialogApi;
  private toast: ToastApi;

  private actions: Record<string, MenuActionEntry> = {
    "check-all": { run: () => this.state.checkAll() },
    "uncheck-all": { run: () => this.state.uncheckAll() },
    "remove-checked": {
      confirmSetting: "confirmRemoveChecked",
      confirmMessage: "Remove checked items?",
      run: () => {
        const count = this.state.markOffList();
        this.toast.show(`Removed ${count} checked item${count !== 1 ? "s" : ""}`);
      },
    },
    "remove-all": {
      confirmSetting: "confirmRemoveAll",
      confirmMessage: "Remove ALL items?",
      run: () => {
        const count = this.state.markAllOffList();
        this.toast.show(`Removed ${count} item${count !== 1 ? "s" : ""}`);
      },
    },
  };

  constructor(state: StateApi, confirm: ConfirmDialogApi, toast: ToastApi) {
    this.state = state;
    this.confirm = confirm;
    this.toast = toast;
  }

  handleAction(action: string): void {
    const entry = this.actions[action];
    if (!entry) return;
    if (entry.confirmSetting && this.state.getSetting(entry.confirmSetting)) {
      const msg = entry.confirmMessage;
      if (!msg) return;
      this.confirm.ask(msg).then((ok) => {
        if (ok) entry.run();
      });
    } else {
      entry.run();
    }
  }
}
